import { ApiResult } from "src/api/types";
import { usePageConfigProviderContext } from "src/features/PageConfigProvider";
import { useTranslation } from "src/hooks";
import { selectCrossMerchant } from "src/reducer/crossMerchantSlice";
import { useAppSelector } from "src/reducer/hooks";
import { enumOperationBatchStatus } from "src/utils/constant";
import {
  displayAmount,
  findChainInfo,
  getDecimal,
  listMappingTransform,
  toDisplayTime,
} from "src/utils/helper";
import { GetListRes } from "../types/GetListRes";
import { TableColumnKey, TableRow } from "../types/Table";
import { WalletType } from "@wallet-manager/node-types/dist/src/postgres/const";
import { getMinerFeeCurrency } from "../../helpers/getMinerFeeCurrency";
import { getMinerFee } from "../../helpers/getMinerFee";

interface UseTableRowsProps {
  apiList: ApiResult<GetListRes>;
}

type MapResultToTupleArray = (
  row: GetListRes[],
  omitKeys: string[],
) => any[][][];

const useTableRows = (props: UseTableRowsProps) => {
  const { translatePrefix } = usePageConfigProviderContext();
  const { merchantMapping } = useAppSelector(selectCrossMerchant);
  const { t, te, translate } = useTranslation(translatePrefix);
  const { apiList } = props;

  const getMerchantName = (merchantId: string | undefined) => {
    if (!merchantId) return "";
    return merchantMapping?.[Number(merchantId)] ?? "";
  };

  const mapResultToTupleArray: MapResultToTupleArray = (rows, omitKeys) => {
    return rows.map((item) => {
      const decimal = getDecimal(item);

      const toAmount = (amount: string | number, dec: string = decimal) =>
        displayAmount(amount, dec);
      const minerFeeDecimal = getDecimal({
        chain_type: item.batch.chain_type,
        chain_id: item.batch.chain_id,
        asset_name: item.batch.miner_fee_asset_name,
      });

      const mappedResult = [
        [TableColumnKey.BatchId, t(TableColumnKey.BatchId), item.batch_id],
        [
          TableColumnKey.MerchantName,
          t(TableColumnKey.MerchantName),
          getMerchantName(item.merchant_id),
        ],
        [
          TableColumnKey.ChainName,
          t(TableColumnKey.ChainName),
          findChainInfo(item.chain_type?.toString(), item.chain_id)?.name,
        ],
        [
          TableColumnKey.Currency,
          t(TableColumnKey.Currency),
          item.from_asset_name,
        ],
        [
          TableColumnKey.SmartAccountType,
          t(TableColumnKey.SmartAccountType),
          item.batch.smart_account_type ? te("yes") : te("no"),
        ],
        [
          TableColumnKey.FreeBandWidthOnly,
          t(TableColumnKey.FreeBandWidthOnly),
          item.tron_free_bandwidth_only ? te("yes") : te("no"),
        ],
        [
          TableColumnKey.SpenderContractVersion,
          t(TableColumnKey.SpenderContractVersion),
          item.spender_contract_version,
        ],
        [
          TableColumnKey.SpenderAddress,
          t(TableColumnKey.SpenderAddress),
          item.spender_address,
        ],
        [
          TableColumnKey.RecipientOwnerAddress,
          t(TableColumnKey.RecipientOwnerAddress),
          item.sweep_dest_owner_address,
        ],
        [
          TableColumnKey.RecipientAddress,
          t(TableColumnKey.RecipientAddress),
          item.sweep_dest_address,
        ],
        [
          TableColumnKey.RecipientWalletType,
          t(TableColumnKey.RecipientWalletType),
          te(WalletType[item.sweep_dest_wallet_type] as any),
        ],
        [
          TableColumnKey.Status,
          t(TableColumnKey.Status),
          translate(
            `enumConstants.${enumOperationBatchStatus[item.batch.status]}`,
          ),
        ],
        [
          TableColumnKey.Currency,
          t(TableColumnKey.Currency),
          item.from_asset_name,
        ],
        [
          TableColumnKey.CurrencyOfMinerFee,
          t(TableColumnKey.CurrencyOfMinerFee),
          getMinerFeeCurrency(
            item.batch.status,
            item.batch.miner_fee_asset_name ?? "",
          ),
        ],
        [
          TableColumnKey.MinerFee,
          t(TableColumnKey.MinerFee),
          getMinerFee(
            item.batch.status,
            toAmount(item?.batch?.miner_fee ?? "", minerFeeDecimal),
          ),
        ],
        [
          TableColumnKey.TotalAmount,
          t(TableColumnKey.TotalAmount),
          toAmount(item.from_amount_total, item.from_asset_decimals),
        ],
        [
          TableColumnKey.ActualAmount,
          t(TableColumnKey.ActualAmount),
          toAmount(item.batch.actual_amount ?? "", item.from_asset_decimals),
        ],
        [
          TableColumnKey.TotalCount,
          t(TableColumnKey.TotalCount),
          item.total_count,
        ],
        [
          TableColumnKey.CreatedBy,
          t(TableColumnKey.CreatedBy),
          item.batch?.created_by,
        ],
        [
          TableColumnKey.CreationTime,
          t(TableColumnKey.CreationTime),
          toDisplayTime(item.created_date),
        ],
        [
          TableColumnKey.LastModifiedTime,
          t(TableColumnKey.LastModifiedTime),
          toDisplayTime(item.last_modified_date),
        ],
        ["rawData", "", item],
      ].filter(([key]) => !omitKeys.includes(key as string));
      return mappedResult;
    });
  };

  const res = mapResultToTupleArray(apiList.rows, []);
  const rows: TableRow[] = res.map(listMappingTransform("key"));

  return {
    rows,
    totalCounts: apiList.count,
    mapResultToTupleArray,
  };
};

export default useTableRows;
